import { useRef, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Zap, Download, Upload, AlertTriangle } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { inputCls, labelCls, btnPrimary, PanelHeader } from "./_shared";

export default function AccountPanel() {
  const { adminApi, admin } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optResult, setOptResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const importFileRef = useRef(null);

  const change = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters.");
    if (next !== confirm) return toast.error("New passwords don't match.");
    setSaving(true);
    try {
      await adminApi.post("/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      setCur(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const optimizeImages = async () => {
    setOptimizing(true);
    setOptResult(null);
    try {
      const { data } = await adminApi.post("/optimize-images");
      setOptResult(data);
      const saved = data?.saved_kb ?? 0;
      toast.success(
        saved > 0
          ? `Site images optimized — freed ${saved.toLocaleString()} KB across the DB.`
          : "All images were already optimized — nothing to shrink."
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOptimizing(false);
    }
  };

  const exportSnapshot = async () => {
    setExporting(true);
    try {
      const res = await adminApi.get("/cms/snapshot", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const d = new Date();
      const ts = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
      a.download = `lc-builders-cms-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Snapshot downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const onImportFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (parsed.version !== 1) {
        toast.error(`Unsupported snapshot version: ${parsed.version}`);
        e.target.value = "";
        return;
      }
      setImportPreview({
        filename: file.name,
        sizeKb: Math.round(file.size / 1024),
        counts: {
          site_content: parsed.site_content?.length ?? 0,
          services: parsed.services?.length ?? 0,
          testimonials: parsed.testimonials?.length ?? 0,
          gallery: parsed.gallery?.length ?? 0,
          hero_media: parsed.hero_media?.length ?? 0,
          gallery_media: parsed.gallery_media?.length ?? 0,
        },
        generatedAt: parsed.generated_at,
        raw: parsed,
      });
    } catch (err) {
      toast.error("Invalid snapshot file — must be JSON exported from this admin panel.");
    } finally {
      e.target.value = "";
    }
  };

  const confirmImport = async () => {
    if (!importPreview) return;
    setImporting(true);
    try {
      const { data } = await adminApi.post("/cms/snapshot/import", {
        snapshot: importPreview.raw,
        confirm: true,
      });
      toast.success(
        `Snapshot imported — ${Object.entries(data.counts || {}).map(([k, v]) => `${k}:${v}`).join(", ")}`
      );
      setImportPreview(null);
      // Give the frontend a beat then force a full refresh so ContentContext repopulates.
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="account-panel">
      <PanelHeader eyebrow="09" title="Account" description="Change your admin password + site maintenance tools." />
      <div className="border border-white/10 bg-[#1A1D21] p-6 max-w-xl">
        <div className="mb-6">
          <div className="text-xs tracking-widest uppercase text-white/50">Signed in as</div>
          <div className="font-display text-xl tracking-wide mt-1">{admin?.email}</div>
        </div>
        <form onSubmit={change} className="space-y-5">
          <div>
            <label className={labelCls}>Current Password</label>
            <input type="password" data-testid="account-current" className={inputCls} value={cur} onChange={(e) => setCur(e.target.value)} required />
          </div>
          <div>
            <label className={labelCls}>New Password (min 8 chars)</label>
            <input type="password" data-testid="account-new" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} />
          </div>
          <div>
            <label className={labelCls}>Confirm New Password</label>
            <input type="password" data-testid="account-confirm" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} />
          </div>
          <button type="submit" disabled={saving} data-testid="account-submit" className={btnPrimary}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Update Password
          </button>
        </form>
      </div>

      <div className="mt-10 border border-white/10 bg-[#1A1D21] p-6 max-w-xl">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="font-display uppercase tracking-widest text-lg">Site Maintenance</div>
            <div className="text-xs tracking-widest uppercase text-[#FF6B00] mt-1">Optimize All Images</div>
          </div>
          <button
            type="button"
            onClick={optimizeImages}
            disabled={optimizing}
            data-testid="account-optimize-images"
            className={btnPrimary}
          >
            {optimizing ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            {optimizing ? "Working…" : "Optimize Now"}
          </button>
        </div>
        <p className="text-sm text-white/60 leading-relaxed">
          Runs image compression across every image already stored in the site — hero, gallery, About, services, testimonials. Downsizes each to a max of 1920px and re-encodes as JPEG. Safe to click repeatedly; images already optimized will be skipped.
        </p>
        {optResult && (
          <div
            data-testid="account-optimize-result"
            className="mt-4 border border-white/10 bg-[#121212] p-4 text-xs text-white/70 font-mono leading-relaxed"
          >
            <div>
              <span className="text-white/40">Total before:</span> {optResult.before_kb.toLocaleString()} KB
            </div>
            <div>
              <span className="text-white/40">Total after:&nbsp;</span> {optResult.after_kb.toLocaleString()} KB
            </div>
            <div className="text-[#FF6B00]">
              Saved: {optResult.saved_kb.toLocaleString()} KB
            </div>
            <div className="mt-2 text-white/40">
              Touched: hero {optResult.images_touched.hero}, about {optResult.images_touched.about}, gallery {optResult.images_touched.gallery}, services {optResult.images_touched.services}, testimonials {optResult.images_touched.testimonials}
            </div>
          </div>
        )}
      </div>

      <div className="mt-10 border border-white/10 bg-[#1A1D21] p-6 max-w-xl" data-testid="cms-snapshot-card">
        <div className="mb-4">
          <div className="font-display uppercase tracking-widest text-lg">CMS Snapshot</div>
          <div className="text-xs tracking-widest uppercase text-[#FF6B00] mt-1">Sync Between Environments</div>
        </div>
        <p className="text-sm text-white/60 leading-relaxed mb-4">
          Preview and production use separate databases. Export a snapshot of everything the CMS holds (hero, gallery, services, testimonials, about, contact, and all media binaries) as a portable JSON file. Import a snapshot to overwrite the current environment with its contents — useful for sharing production state with support, or copying production content back into preview.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={exportSnapshot}
            disabled={exporting}
            data-testid="cms-snapshot-export"
            className={btnPrimary}
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {exporting ? "Exporting…" : "Export Snapshot"}
          </button>
          <button
            type="button"
            onClick={() => importFileRef.current?.click()}
            disabled={importing}
            data-testid="cms-snapshot-import-pick"
            className="inline-flex items-center gap-2 border border-white/20 hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors px-5 py-3 font-display tracking-widest uppercase text-white text-sm"
          >
            <Upload size={14} />
            Import Snapshot
          </button>
          <input
            ref={importFileRef}
            type="file"
            accept="application/json,.json"
            onChange={onImportFilePicked}
            className="hidden"
            data-testid="cms-snapshot-file-input"
          />
        </div>

        {importPreview && (
          <div
            data-testid="cms-snapshot-import-preview"
            className="mt-5 border border-[#FF6B00]/40 bg-[#121212] p-4"
          >
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle size={18} className="text-[#FF6B00] mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <div className="text-white font-medium">Confirm import — this replaces ALL current CMS content.</div>
                <div className="text-white/50 text-xs mt-1">Admins, user accounts, and lead submissions are NOT touched.</div>
              </div>
            </div>
            <div className="text-xs text-white/70 font-mono leading-relaxed">
              <div><span className="text-white/40">File:</span> {importPreview.filename} ({importPreview.sizeKb.toLocaleString()} KB)</div>
              {importPreview.generatedAt && (
                <div><span className="text-white/40">Generated:</span> {importPreview.generatedAt}</div>
              )}
              <div className="mt-2 text-white/40">Will restore:</div>
              <div>&nbsp;&nbsp;site_content: {importPreview.counts.site_content}</div>
              <div>&nbsp;&nbsp;services: {importPreview.counts.services}</div>
              <div>&nbsp;&nbsp;testimonials: {importPreview.counts.testimonials}</div>
              <div>&nbsp;&nbsp;gallery: {importPreview.counts.gallery}</div>
              <div>&nbsp;&nbsp;hero_media: {importPreview.counts.hero_media}</div>
              <div>&nbsp;&nbsp;gallery_media: {importPreview.counts.gallery_media}</div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={confirmImport}
                disabled={importing}
                data-testid="cms-snapshot-import-confirm"
                className="inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#E65A00] transition-colors px-5 py-2.5 font-display tracking-widest uppercase text-white text-xs disabled:opacity-60"
              >
                {importing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                {importing ? "Importing…" : "Yes, Overwrite"}
              </button>
              <button
                type="button"
                onClick={() => setImportPreview(null)}
                disabled={importing}
                data-testid="cms-snapshot-import-cancel"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 transition-colors px-5 py-2.5 font-display tracking-widest uppercase text-white/70 text-xs"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
